import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';

// Analytics events collection
const analyticsCollection = 'analytics_events';

/**
 * Track a store view event
 * @param {string} storeId - The store ID
 * @param {string} sessionId - Unique session identifier
 * @param {string} userId - User ID if authenticated
 */
export async function trackStoreView(storeId, sessionId, userId = null) {
  try {
    await addDoc(collection(db, analyticsCollection), {
      event_type: 'store_viewed',
      storeId,
      sessionId,
      userId,
      timestamp: serverTimestamp(),
      metadata: {
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    });
    console.log('✅ Analytics: Store view tracked successfully');
  } catch (error) {
    console.warn('⚠️ Analytics: Could not track store view (non-critical):', error.message);
    // Don't throw - this is non-critical for store functionality
  }
}

/**
 * Track a product view event
 * @param {string} productId - The product ID
 * @param {string} storeId - The store ID
 * @param {string} sessionId - Unique session identifier
 * @param {string} userId - User ID if authenticated
 */
export async function trackProductView(productId, storeId, sessionId, userId = null) {
  try {
    await addDoc(collection(db, analyticsCollection), {
      event_type: 'product_viewed',
      productId,
      storeId,
      sessionId,
      userId,
      timestamp: serverTimestamp(),
      metadata: {
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    });
    console.log('✅ Analytics: Product view tracked successfully');
  } catch (error) {
    console.warn('⚠️ Analytics: Could not track product view (non-critical):', error.message);
    // Don't throw - this is non-critical for product functionality
  }
}

/**
 * Track a product added to cart event
 * @param {string} productId - The product ID
 * @param {string} storeId - The store ID
 * @param {string} sessionId - Unique session identifier
 * @param {string} userId - User ID if authenticated
 */
export async function trackAddToCart(productId, storeId, sessionId, userId = null) {
  try {
    await addDoc(collection(db, analyticsCollection), {
      event_type: 'product_added_to_cart',
      productId,
      storeId,
      sessionId,
      userId,
      timestamp: serverTimestamp(),
      metadata: {
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    });
    console.log('✅ Analytics: Add to cart tracked successfully');
  } catch (error) {
    console.warn('⚠️ Analytics: Could not track add to cart (non-critical):', error.message);
    // Don't throw - this is non-critical for cart functionality
  }
}

/**
 * Track an order completion event
 * @param {Array<string>} productIds - Array of product IDs in the order
 * @param {string} storeId - The store ID
 * @param {string} orderId - The order ID
 * @param {string} userId - User ID if authenticated
 */
export async function trackOrderCompleted(productIds, storeId, orderId, userId = null) {
  try {
    await addDoc(collection(db, analyticsCollection), {
      event_type: 'order_completed',
      productIds,
      storeId,
      orderId,
      userId,
      timestamp: serverTimestamp(),
      metadata: {
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    });
    console.log('✅ Analytics: Order completion tracked successfully');
  } catch (error) {
    console.warn('⚠️ Analytics: Could not track order completion (non-critical):', error.message);
    // Don't throw - this is non-critical for order functionality
  }
}

/**
 * Get seller analytics data for a specific date range
 * @param {string} sellerId - The seller's user ID
 * @param {string} dateRange - Date range: '7d', '30d', or 'all'
 * @returns {Object} Aggregated analytics data
 */
export async function getSellerAnalytics(sellerId, dateRange = '30d') {
  try {
    // First, get the seller's store ID
    const userDoc = await getDoc(doc(db, 'users', sellerId));
    if (!userDoc.exists()) {
      throw new Error('Seller not found');
    }
    
    const userData = userDoc.data();
    let storeId = userData.storeId;
    
    // If no storeId in user doc, try to find store by sellerId
    if (!storeId) {
      console.log('No storeId in user doc, searching for store by sellerId...');
      const storesQuery = query(
        collection(db, 'shops'),
        where('sellerId', '==', sellerId)
      );
      const storesSnapshot = await getDocs(storesQuery);
      
      if (!storesSnapshot.empty) {
        storeId = storesSnapshot.docs[0].id;
        console.log('Found store by sellerId:', storeId);
      } else {
        // Return empty analytics data instead of throwing error
        console.log('No store found for seller, returning empty analytics');
        return {
          success: true,
          data: {
            store: {
              views: 0,
              totalOrders: 0,
              conversionRate: 0
            },
            products: [],
            uniqueSessions: 0
          },
          dateRange,
          totalEvents: 0,
          message: 'No store found. Create a store to start tracking analytics.'
        };
      }
    }

    // Calculate date cutoff
    let cutoffDate = null;
    if (dateRange === '7d') {
      cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 7);
    } else if (dateRange === '30d') {
      cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 30);
    }

    // Build query
    let q = query(
      collection(db, analyticsCollection),
      where('storeId', '==', storeId)
    );

    if (cutoffDate) {
      q = query(q, where('timestamp', '>=', cutoffDate));
    }

    q = query(q, orderBy('timestamp', 'desc'));

    const snapshot = await getDocs(q);
    const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Aggregate data
    const analytics = aggregateAnalyticsData(events, storeId);
    
    return {
      success: true,
      data: analytics,
      dateRange,
      totalEvents: events.length
    };

  } catch (error) {
    console.error('Error getting seller analytics:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
}

/**
 * Aggregate analytics events into meaningful metrics
 * @param {Array} events - Array of analytics events
 * @param {string} storeId - The store ID
 * @returns {Object} Aggregated metrics
 */
function aggregateAnalyticsData(events, storeId) {
  const metrics = {
    store: {
      views: 0,
      totalOrders: 0,
      conversionRate: 0
    },
    products: {},
    sessions: new Set()
  };

  // Process each event
  events.forEach(event => {
    const sessionId = event.sessionId;
    metrics.sessions.add(sessionId);

    switch (event.event_type) {
      case 'store_viewed':
        metrics.store.views++;
        break;
      
      case 'product_viewed':
        if (event.productId) {
          if (!metrics.products[event.productId]) {
            metrics.products[event.productId] = {
              views: 0,
              addToCarts: 0,
              orders: 0,
              productConversionRate: 0,
              cartConversionRate: 0
            };
          }
          metrics.products[event.productId].views++;
        }
        break;
      
      case 'product_added_to_cart':
        if (event.productId) {
          if (!metrics.products[event.productId]) {
            metrics.products[event.productId] = {
              views: 0,
              addToCarts: 0,
              orders: 0,
              productConversionRate: 0,
              cartConversionRate: 0
            };
          }
          metrics.products[event.productId].addToCarts++;
        }
        break;
      
      case 'order_completed':
        metrics.store.totalOrders++;
        if (event.productIds) {
          event.productIds.forEach(productId => {
            if (!metrics.products[productId]) {
              metrics.products[productId] = {
                views: 0,
                addToCarts: 0,
                orders: 0,
                productConversionRate: 0,
                cartConversionRate: 0
              };
            }
            metrics.products[productId].orders++;
          });
        }
        break;
    }
  });

  // Calculate conversion rates
  if (metrics.store.views > 0) {
    metrics.store.conversionRate = (metrics.store.totalOrders / metrics.store.views) * 100;
  }

  // Calculate product-level conversion rates
  Object.keys(metrics.products).forEach(productId => {
    const product = metrics.products[productId];
    
    if (product.views > 0) {
      product.productConversionRate = (product.orders / product.views) * 100;
    }
    
    if (product.addToCarts > 0) {
      product.cartConversionRate = (product.orders / product.addToCarts) * 100;
    }
  });

  // Convert products object to array for easier frontend consumption
  const productsArray = Object.keys(metrics.products).map(productId => ({
    productId,
    ...metrics.products[productId]
  }));

  return {
    store: metrics.store,
    products: productsArray,
    uniqueSessions: metrics.sessions.size
  };
}

/**
 * Generate a unique session ID for tracking
 * @returns {string} Unique session identifier
 */
export function generateSessionId() {
  // Check if session ID already exists
  let sessionId = sessionStorage.getItem('vesta_session_id');
  
  if (!sessionId) {
    // Generate new session ID
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('vesta_session_id', sessionId);
  }
  
  return sessionId;
}
